import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { businessCardService, ExtractedData, StakeholderMatch } from '../../services/businessCardService';
import { stakeholderService } from '../../services/stakeholderService';
import { ExtractedDataPreview } from './ExtractedDataPreview';
import { BusinessCardScanner } from './BusinessCardScanner';
import { DuplicateReviewSheet } from './DuplicateReviewSheet';
import { Button, Dialog, DialogFooter, IconButton } from '../ui';
import { MatchBeforeCreate } from '../../features/duplicates/components/MatchBeforeCreate';
import { blockingHits, useMatchProbe } from '../../features/duplicates/useDuplicates';
import { duplicatesErrorMessage } from '../../features/duplicates/duplicatesApi';
import { entityHref } from '../../features/duplicates/format';
import type { MatchHit, MatchProbe } from '../../types/duplicates';
import { useMediaCapture, CropRegion } from '../../hooks/useMediaCapture';
import { Camera, CameraOff, Check, Images, RotateCcw, X, Zap, ZapOff } from 'lucide-react';

// Standard business card aspect ratio (85.6mm × 53.98mm ≈ 1.586:1)
const CARD_ASPECT_RATIO = 1.586;
// How much of the camera view width the card frame takes up
const CARD_FRAME_WIDTH_PERCENT = 0.92;

type FocusIndicator = { x: number; y: number; id: number };

interface BusinessCardImportProps {
  onImport: (stakeholderData: any) => void;
  onMerge?: (stakeholderId: string, mergedData: Record<string, any>) => Promise<void>;
  onClose: () => void;
}

type Step = 'capture' | 'extract' | 'match' | 'preview';

/**
 * How long the directory check may hold up an import. Someone is standing
 * there with a card; past this the import carries on as if nothing matched.
 */
const PROBE_BUDGET_MS = 3500;

/** Fields the card can fill in on a record that already exists */
const FILLABLE_FIELDS = [
  'firstName',
  'lastName',
  'professionalEmail',
  'personalEmail',
  'professionalPhone',
  'personalPhone',
  'location',
  'linkedinUrl',
  'twitterUrl',
  'facebookUrl',
  'telegramUrl',
  'whatsapp',
] as const;

/** What the server needs to say whether this person is already on file */
export function buildMatchProbe(data: ExtractedData): MatchProbe {
  const name = [data.firstName, data.lastName].filter(Boolean).join(' ').trim() || data.fullName || null;
  const social = [data.linkedinUrl, data.twitterUrl, data.facebookUrl, data.telegramUrl].filter(Boolean) as string[];
  return {
    entityType: 'stakeholder',
    name,
    firstName: data.firstName || null,
    lastName: data.lastName || null,
    email: data.professionalEmail || data.personalEmail || null,
    phone: data.professionalPhone || data.personalPhone || null,
    organizationName: data.organization || null,
    role: data.role || null,
    website: data.website || null,
    social: social.length ? social : null,
  };
}

export const BusinessCardImport: React.FC<BusinessCardImportProps> = ({ onImport, onMerge, onClose }) => {
  const [step, setStep] = useState<Step>('capture');
  const [photoSource, setPhotoSource] = useState<'camera' | 'library'>('camera');
  const [extractedData, setExtractedData] = useState<ExtractedData | null>(null);
  const [businessCardImageId, setBusinessCardImageId] = useState<string | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorType, setErrorType] = useState<'api' | 'network' | 'auth' | 'unknown'>('unknown');
  const [retryCount, setRetryCount] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [duplicateMatches, setDuplicateMatches] = useState<StakeholderMatch[]>([]);
  const [showDuplicateReview, setShowDuplicateReview] = useState(false);
  const [pendingStakeholderData, setPendingStakeholderData] = useState<Record<string, any> | null>(null);
  const [importing, setImporting] = useState(false);
  const [matchHits, setMatchHits] = useState<MatchHit[]>([]);
  const [selectedMatchId, setSelectedMatchId] = useState<string>('');
  const [attachingToMatch, setAttachingToMatch] = useState(false);
  const [matchError, setMatchError] = useState<string | null>(null);
  // Set once the reviewer has already been shown the matches and pressed on
  const [matchAnswered, setMatchAnswered] = useState(false);

  const navigate = useNavigate();
  const { probe, reset: resetProbe } = useMatchProbe();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const maxRetries = 3;

  const {
    videoRef,
    canvasRef,
    cameraActive,
    cameraError,
    startCamera,
    stopCamera,
    capturePhoto,
    focusAt,
    torchSupported,
    torchOn,
    toggleTorch,
    handleFileSelect,
    previewUrl,
    previewFile,
    clearPreview,
    processing: imageProcessing,
    processingMessage,
  } = useMediaCapture({ maxFileSizeMB: 10, maxImageDimension: 2048 });

  const [focusIndicator, setFocusIndicator] = useState<FocusIndicator | null>(null);
  const [shutterFlash, setShutterFlash] = useState(false);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const focusIdRef = useRef(0);

  // Camera lifecycle: run the camera whenever the full-screen viewfinder is
  // showing; stop it during photo review/processing and on the other steps.
  useEffect(() => {
    if (step === 'capture' && !previewUrl && !imageProcessing) {
      if (!cameraActive && !cameraError) startCamera();
    } else {
      stopCamera();
    }
  }, [step, previewUrl, imageProcessing, cameraActive, cameraError, startCamera, stopCamera]);

  // The capture step renders outside the Dialog, so it manages its own
  // body scroll lock and Escape-to-close.
  useEffect(() => {
    if (step !== 'capture') return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [step, onClose]);

  // The crop region is computed from the *actual* on-screen frame at capture
  // time. We measure the rendered frame against the video's intrinsic size so
  // the crop matches what the user sees even when `object-cover` scales/crops
  // the video stream within the container.
  const computeCropRegion = useCallback((): CropRegion => {
    const video = videoRef.current;
    const container = videoContainerRef.current;
    if (!video || !container || !video.videoWidth || !video.videoHeight) {
      // Fallback: center crop matching card aspect at frame width %
      const frameW = CARD_FRAME_WIDTH_PERCENT;
      const frameH = frameW / CARD_ASPECT_RATIO;
      return { x: (1 - frameW) / 2, y: (1 - frameH) / 2, width: frameW, height: frameH };
    }

    const containerRect = container.getBoundingClientRect();
    const cW = containerRect.width;
    const cH = containerRect.height;
    const vW = video.videoWidth;
    const vH = video.videoHeight;

    // object-cover: scale so the video fills the container, cropping overflow.
    const scale = Math.max(cW / vW, cH / vH);
    const drawnW = vW * scale;
    const drawnH = vH * scale;
    const offsetX = (cW - drawnW) / 2; // negative — pixels of video cropped off left/right
    const offsetY = (cH - drawnH) / 2;

    // Frame in container coords (centered)
    const frameWpx = cW * CARD_FRAME_WIDTH_PERCENT;
    const frameHpx = frameWpx / CARD_ASPECT_RATIO;
    // Clamp height to container
    const finalFrameHpx = Math.min(frameHpx, cH * 0.92);
    const finalFrameWpx = finalFrameHpx * CARD_ASPECT_RATIO;
    const frameXpx = (cW - finalFrameWpx) / 2;
    const frameYpx = (cH - finalFrameHpx) / 2;

    // Map container-px → video-px
    const srcX = (frameXpx - offsetX) / scale;
    const srcY = (frameYpx - offsetY) / scale;
    const srcW = finalFrameWpx / scale;
    const srcH = finalFrameHpx / scale;

    return {
      x: Math.max(0, Math.min(1, srcX / vW)),
      y: Math.max(0, Math.min(1, srcY / vH)),
      width: Math.max(0, Math.min(1, srcW / vW)),
      height: Math.max(0, Math.min(1, srcH / vH)),
    };
  }, [videoRef]);

  // Capture cropped to the on-screen card frame.
  const handleCapture = useCallback(async () => {
    setShutterFlash(true);
    setTimeout(() => setShutterFlash(false), 180);
    // Haptic tick on devices that support it (Android)
    navigator.vibrate?.(15);
    setPhotoSource('camera');
    await capturePhoto(computeCropRegion());
  }, [capturePhoto, computeCropRegion]);

  // Tap-to-focus
  const handleVideoTap = useCallback(async (e: React.PointerEvent<HTMLDivElement>) => {
    const container = videoContainerRef.current;
    if (!container || !cameraActive) return;
    const rect = container.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    const fx = Math.max(0, Math.min(1, px / rect.width));
    const fy = Math.max(0, Math.min(1, py / rect.height));

    focusIdRef.current += 1;
    setFocusIndicator({ x: px, y: py, id: focusIdRef.current });
    void focusAt(fx, fy);
    const id = focusIdRef.current;
    setTimeout(() => {
      setFocusIndicator(curr => (curr && curr.id === id ? null : curr));
    }, 900);
  }, [focusAt, cameraActive]);

  // Handle file input change
  const handleFileInputChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoSource('library');
      await handleFileSelect(file);
    }
    // Reset input
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [handleFileSelect]);

  // Retake / choose another from the review screen
  const handleRetake = useCallback(() => {
    const fromLibrary = photoSource === 'library';
    clearPreview();
    if (fromLibrary) {
      // Reopen the native picker directly — "Choose Another" should not bounce
      // the user through the camera first.
      fileInputRef.current?.click();
    }
  }, [photoSource, clearPreview]);

  // Handle drag and drop
  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      setPhotoSource('library');
      await handleFileSelect(file);
    }
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  /**
   * Between reading the card and filling the form: ask whether this person is
   * already on file. The check is given a short budget and may fail outright —
   * either way the import continues, it just does not stop to ask.
   */
  const afterExtraction = useCallback(async (data: ExtractedData) => {
    setMatchError(null);
    const hits = blockingHits(await probe(buildMatchProbe(data), PROBE_BUDGET_MS));
    if (hits.length) {
      setMatchHits(hits);
      setSelectedMatchId(hits[0].id);
      setStep('match');
      return;
    }
    setMatchHits([]);
    setStep('preview');
  }, [probe]);

  // Extract data from image
  const extractData = useCallback(async () => {
    if (!previewFile) return;

    setStep('extract');
    setExtracting(true);
    setError(null);

    try {
      const result = await businessCardService.extractFromImage(previewFile);
      setExtractedData(result.extractedData);
      setBusinessCardImageId(result.businessCardImageId);
      setRetryCount(0);
      await afterExtraction(result.extractedData);
    } catch (err: any) {
      let errorMessage = 'Failed to extract data from business card';
      if (typeof err === 'string') {
        errorMessage = err;
      } else if (err?.message) {
        errorMessage = String(err.message);
      }

      let errorCategory: 'api' | 'network' | 'auth' | 'unknown' = 'unknown';
      if (errorMessage.includes('API key') || errorMessage.includes('authentication') || errorMessage.includes('401')) {
        errorCategory = 'auth';
      } else if (errorMessage.includes('connect') || errorMessage.includes('network') || errorMessage.includes('ECONNREFUSED')) {
        errorCategory = 'network';
      } else if (errorMessage.includes('rate limit') || errorMessage.includes('429') || errorMessage.includes('JSON')) {
        errorCategory = 'api';
      }

      setError(errorMessage);
      setErrorType(errorCategory);
    } finally {
      setExtracting(false);
    }
  }, [previewFile, afterExtraction]);

  // Retry extraction
  const handleRetry = useCallback(async () => {
    if (retryCount < maxRetries) {
      setRetryCount(prev => prev + 1);
      setError(null);
      setExtracting(true);

      try {
        if (!previewFile) return;
        const result = await businessCardService.extractFromImage(previewFile);
        setExtractedData(result.extractedData);
        setBusinessCardImageId(result.businessCardImageId);
        setRetryCount(0);
        await afterExtraction(result.extractedData);
      } catch (err: any) {
        let errorMessage = 'Failed to extract data from business card';
        if (err?.message) errorMessage = String(err.message);

        let errorCategory: 'api' | 'network' | 'auth' | 'unknown' = 'unknown';
        if (errorMessage.includes('API key') || errorMessage.includes('authentication')) {
          errorCategory = 'auth';
        } else if (errorMessage.includes('connect') || errorMessage.includes('network')) {
          errorCategory = 'network';
        }

        setError(errorMessage);
        setErrorType(errorCategory);
      } finally {
        setExtracting(false);
      }
    }
  }, [retryCount, maxRetries, previewFile, afterExtraction]);

  // Try with another image
  const handleTryAnother = useCallback(() => {
    setError(null);
    setRetryCount(0);
    setMatchHits([]);
    setMatchError(null);
    setMatchAnswered(false);
    resetProbe();
    clearPreview();
    setStep('capture');
  }, [clearPreview, resetProbe]);

  // Handle field edit in preview
  const handleFieldEdit = useCallback((field: string, value: string) => {
    if (extractedData) {
      setExtractedData({ ...extractedData, [field]: value });
    }
  }, [extractedData]);

  // Build stakeholder data from extracted data
  const buildStakeholderData = useCallback(() => {
    if (!extractedData) return null;

    return {
      firstName: extractedData.firstName || '',
      lastName: extractedData.lastName || '',
      professionalEmail: extractedData.professionalEmail || '',
      personalEmail: extractedData.personalEmail || '',
      professionalPhone: extractedData.professionalPhone || '',
      personalPhone: extractedData.personalPhone || '',
      location: extractedData.location || '',
      linkedinUrl: extractedData.linkedinUrl || '',
      twitterUrl: extractedData.twitterUrl || '',
      facebookUrl: extractedData.facebookUrl || '',
      telegramUrl: extractedData.telegramUrl || '',
      whatsapp: extractedData.whatsapp || '',
      website: extractedData.website || '',
      bio: `Imported from business card. Confidence: ${extractedData.confidence.overall}%`,
      businessCardMetadata: {
        imageId: businessCardImageId,
        extractedFields: extractedData,
        confidence: extractedData.confidence,
      },
      category: (extractedData as any).category || '',
      role: extractedData.role || '',
      department: extractedData.department || '',
      startDate: new Date().toISOString().split('T')[0],
      isCurrent: true,
      organizationName: extractedData.organization || '',
    };
  }, [extractedData, businessCardImageId]);

  // ── Already in the directory? ──

  const handleOpenExisting = useCallback((hit: MatchHit) => {
    onClose();
    navigate(entityHref('stakeholder', hit.id));
  }, [navigate, onClose]);

  /**
   * Keep the record that exists: attach the card photo if it has none and fill
   * in only the fields it is missing. Nothing already on file is overwritten.
   */
  const handleAttachToExisting = useCallback(async (hit: MatchHit) => {
    if (!onMerge) return;
    const card = buildStakeholderData();
    if (!card) return;

    setAttachingToMatch(true);
    setMatchError(null);
    try {
      const existing: Record<string, any> = (await stakeholderService.getStakeholder(hit.id)) as any;
      const filled: Record<string, any> = {};
      FILLABLE_FIELDS.forEach((key) => {
        const value = String(card[key] ?? '').trim();
        const current = String(existing?.[key] ?? '').trim();
        if (value && !current) filled[key] = value;
      });
      // The card photo becomes the picture only when there is not one already
      if (businessCardImageId && !existing?.profileImageId) filled.profileImageId = businessCardImageId;

      await onMerge(hit.id, filled);
      onClose();
    } catch (err) {
      setMatchError(duplicatesErrorMessage(err));
    } finally {
      setAttachingToMatch(false);
    }
  }, [onMerge, buildStakeholderData, businessCardImageId, onClose]);

  const handleCreateAnyway = useCallback(() => {
    setMatchAnswered(true);
    setMatchError(null);
    setStep('preview');
  }, []);

  // Handle import — check for duplicates first
  const handleImport = useCallback(async () => {
    const stakeholderData = buildStakeholderData();
    if (!stakeholderData) return;

    // Already asked about these matches before the form — do not ask twice
    if (matchAnswered) {
      onImport(stakeholderData);
      return;
    }

    // Check for duplicates (non-blocking — if it fails, proceed with import)
    try {
      const matches = await businessCardService.matchStakeholder({
        firstName: stakeholderData.firstName,
        lastName: stakeholderData.lastName,
        professionalEmail: stakeholderData.professionalEmail,
        personalEmail: stakeholderData.personalEmail,
        professionalPhone: stakeholderData.professionalPhone,
        personalPhone: stakeholderData.personalPhone,
      });

      if (matches.length > 0 && onMerge) {
        // Duplicates found — show review sheet
        setDuplicateMatches(matches);
        setPendingStakeholderData(stakeholderData);
        setShowDuplicateReview(true);
        return;
      }
    } catch {
      // Non-blocking: proceed with import on failure
    }

    onImport(stakeholderData);
  }, [buildStakeholderData, onImport, onMerge, matchAnswered]);

  // Handle skip from duplicate review — proceed with normal import
  const handleDuplicateSkip = useCallback(() => {
    setShowDuplicateReview(false);
    if (pendingStakeholderData) {
      onImport(pendingStakeholderData);
    }
  }, [pendingStakeholderData, onImport]);

  // Handle merge from duplicate review
  const handleDuplicateMerge = useCallback(async (stakeholderId: string, mergedData: Record<string, any>) => {
    if (onMerge) {
      await onMerge(stakeholderId, mergedData);
    }
    setShowDuplicateReview(false);
    onClose();
  }, [onMerge, onClose]);

  // ── Render Steps ──

  // Full-screen, native-style capture experience. Rendered through a portal
  // (outside the Dialog) so no ancestor transform breaks position:fixed,
  // and the shutter bar is always pinned on screen — never scrolled away.
  const renderCaptureOverlay = () => {
    const reviewing = Boolean(previewUrl);

    return (
      <div
        className="fixed inset-0 z-[60] bg-black font-ui antialiased select-none"
        role="dialog"
        aria-modal="true"
        aria-label="Scan business card"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        {/* Stage: full-bleed on mobile, centered phone-shaped panel on desktop */}
        <div className="w-full h-full flex items-center justify-center">
          <div
            ref={videoContainerRef}
            onPointerDown={handleVideoTap}
            className="relative w-full h-full sm:h-[85vh] sm:w-auto sm:aspect-[3/4] sm:max-w-[95vw] sm:rounded-2xl overflow-hidden bg-black"
            style={{ touchAction: 'manipulation' }}
          >
            {/* Live viewfinder */}
            {!reviewing && !imageProcessing && (
              <>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="absolute inset-0 w-full h-full object-cover"
                />

                {/* Card frame overlay */}
                {cameraActive && (
                  <div className="absolute inset-0 pointer-events-none">
                    {/* Dark surround built from a single dark layer with a centered
                        aspect-ratio "hole" stamped via box-shadow. */}
                    <div
                      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 card-frame-hole"
                      style={{
                        width: `${CARD_FRAME_WIDTH_PERCENT * 100}%`,
                        aspectRatio: `${CARD_ASPECT_RATIO}`,
                      }}
                    />

                    {/* Static card frame border */}
                    <div
                      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 card-frame-animate"
                      style={{
                        width: `${CARD_FRAME_WIDTH_PERCENT * 100}%`,
                        aspectRatio: `${CARD_ASPECT_RATIO}`,
                      }}
                    >
                      <div className="absolute -top-[2px] -left-[2px] w-9 h-9 border-t-[3px] border-l-[3px] border-white rounded-tl-xl card-corner-pulse" style={{ filter: 'drop-shadow(0 0 6px rgba(255,255,255,0.6))' }} />
                      <div className="absolute -top-[2px] -right-[2px] w-9 h-9 border-t-[3px] border-r-[3px] border-white rounded-tr-xl card-corner-pulse" style={{ filter: 'drop-shadow(0 0 6px rgba(255,255,255,0.6))' }} />
                      <div className="absolute -bottom-[2px] -left-[2px] w-9 h-9 border-b-[3px] border-l-[3px] border-white rounded-bl-xl card-corner-pulse" style={{ filter: 'drop-shadow(0 0 6px rgba(255,255,255,0.6))' }} />
                      <div className="absolute -bottom-[2px] -right-[2px] w-9 h-9 border-b-[3px] border-r-[3px] border-white rounded-br-xl card-corner-pulse" style={{ filter: 'drop-shadow(0 0 6px rgba(255,255,255,0.6))' }} />
                      <div className="absolute inset-0 rounded-xl border border-white/40" />
                    </div>
                  </div>
                )}

                {/* Camera starting */}
                {!cameraActive && !cameraError && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-6 h-6 border-[1.5px] border-white/40 border-t-white rounded-full animate-spin" />
                  </div>
                )}

                {/* Camera unavailable — choose a photo / upload instead, or retry the camera */}
                {cameraError && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center px-8">
                    <div className="flex w-full max-w-[272px] flex-col items-center text-center">
                      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10 text-white/60">
                        <CameraOff className="h-6 w-6" strokeWidth={1.5} />
                      </span>
                      <p className="mt-4 text-[15px] leading-snug tracking-[-0.01em] text-white/90">{cameraError}</p>
                      <div className="mt-7 flex w-full flex-col gap-2">
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="flex h-12 items-center justify-center gap-2 rounded-full bg-white text-[15px] font-medium text-ink transition-transform active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
                        >
                          <Images className="h-[18px] w-[18px]" strokeWidth={1.75} />
                          <span className="sm:hidden">Choose photo</span>
                          <span className="hidden sm:inline">Upload image</span>
                        </button>
                        <button
                          type="button"
                          onClick={startCamera}
                          className="flex h-12 items-center justify-center gap-2 rounded-full bg-white/10 text-[15px] text-white transition-transform active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
                        >
                          <Camera className="h-[18px] w-[18px]" strokeWidth={1.75} />
                          Try camera
                        </button>
                      </div>
                      <p className="mt-4 hidden text-[12px] text-white/40 sm:block">or drop an image here</p>
                    </div>
                  </div>
                )}

                {/* Tap-to-focus indicator */}
                {focusIndicator && (
                  <div
                    key={focusIndicator.id}
                    className="absolute focus-ring"
                    style={{
                      left: focusIndicator.x - 32,
                      top: focusIndicator.y - 32,
                      width: 64,
                      height: 64,
                    }}
                  />
                )}

                {/* Shutter flash */}
                {shutterFlash && <div className="absolute inset-0 bg-white shutter-flash" />}
              </>
            )}

            {/* Photo review — native camera-roll style confirmation */}
            {reviewing && (
              <div className="absolute inset-0 flex items-center justify-center bg-black">
                <img
                  src={previewUrl!}
                  alt="Selected business card"
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            )}

            {/* Image processing (HEIC conversion / compression) */}
            {imageProcessing && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black">
                <div className="w-6 h-6 border-[1.5px] border-white/30 border-t-white rounded-full animate-spin" />
                <p className="mt-3 text-[13px] text-white/70">{processingMessage || 'Processing image…'}</p>
              </div>
            )}

            {/* Edge gradients so controls stay legible over any scene */}
            <div className="absolute top-0 left-0 right-0 h-28 bg-gradient-to-b from-black/60 to-transparent pointer-events-none" />
            <div className="absolute bottom-0 left-0 right-0 h-44 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />

            {/* Top bar: close + torch (safe-area aware); above the camera error panel so Close stays tappable */}
            <div
              className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-3"
              style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <button
                onClick={onClose}
                aria-label="Close"
                className="w-11 h-11 rounded-full bg-black/40 backdrop-blur-md text-white flex items-center justify-center active:scale-95 transition-transform"
              >
                <X className="h-[22px] w-[22px]" strokeWidth={1.75} />
              </button>
              {!reviewing && !imageProcessing && torchSupported && !cameraError ? (
                <button
                  onClick={() => { void toggleTorch(); }}
                  aria-label={torchOn ? 'Turn flash off' : 'Turn flash on'}
                  className="w-11 h-11 rounded-full bg-black/40 backdrop-blur-md text-white flex items-center justify-center active:scale-95 transition-transform"
                >
                  {torchOn
                    ? <Zap className="h-5 w-5 text-amber-300" strokeWidth={1.75} fill="currentColor" />
                    : <ZapOff className="h-5 w-5" strokeWidth={1.75} />}
                </button>
              ) : (
                <div className="w-11 h-11" />
              )}
            </div>

            {/* Bottom controls (safe-area aware, always on screen) */}
            <div
              className="absolute bottom-0 left-0 right-0 flex flex-col items-center gap-5 px-6"
              style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
              onPointerDown={(e) => e.stopPropagation()}
            >
              {reviewing ? (
                <div className="w-full max-w-sm flex items-center gap-3">
                  <button
                    onClick={handleRetake}
                    className="flex-1 h-12 rounded-full bg-white/15 backdrop-blur-md text-white text-[15px] flex items-center justify-center gap-2 active:scale-95 transition-transform"
                  >
                    <RotateCcw className="h-4 w-4" strokeWidth={1.75} />
                    {photoSource === 'library' ? 'Choose another' : 'Retake'}
                  </button>
                  <button
                    onClick={extractData}
                    className="flex-1 h-12 rounded-full bg-primary-600 text-white text-[15px] font-medium flex items-center justify-center gap-2 active:scale-95 transition-transform"
                  >
                    <Check className="h-4 w-4" strokeWidth={2} />
                    Use photo
                  </button>
                </div>
              ) : !imageProcessing && !cameraError ? (
                <>
                  {cameraActive && (
                    <span className="inline-block px-3.5 py-1.5 rounded-full backdrop-blur-md text-[12px] bg-black/55 text-white/90">
                      Fit the card in the frame · tap to focus
                    </span>
                  )}
                  <div className="w-full flex items-center justify-between">
                    {/* Photo library — opens the native OS picker */}
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      aria-label="Choose from photo library"
                      className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur-md text-white flex items-center justify-center active:scale-95 transition-transform"
                    >
                      <Images className="h-[22px] w-[22px]" strokeWidth={1.75} />
                    </button>
                    {/* Shutter */}
                    <button
                      onClick={handleCapture}
                      disabled={!cameraActive}
                      aria-label="Capture photo"
                      className="w-[76px] h-[76px] rounded-full border-4 border-white flex items-center justify-center active:scale-95 transition-transform disabled:opacity-40"
                    >
                      <div className="w-[60px] h-[60px] rounded-full bg-white shutter-inner" />
                    </button>
                    {/* Spacer keeps the shutter perfectly centered */}
                    <div className="w-12 h-12" />
                  </div>
                </>
              ) : null}
            </div>

            {/* Hidden canvas for capture */}
            <canvas ref={canvasRef} className="hidden" />
          </div>
        </div>

        {/* Desktop drag & drop highlight */}
        {isDragging && (
          <div className="absolute inset-3 rounded-[28px] border-[1.5px] border-dashed border-white/60 pointer-events-none" />
        )}

        {/* Native photo picker */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileInputChange}
          className="hidden"
        />
      </div>
    );
  };

  const renderExtractStep = () => {
    if (!previewUrl) return null;

    return (
      <BusinessCardScanner
        imageUrl={previewUrl}
        isProcessing={extracting}
        error={error}
        errorType={errorType}
        onRetry={handleRetry}
        onTryAnother={handleTryAnother}
        retryCount={retryCount}
        maxRetries={maxRetries}
      />
    );
  };

  const handleImportClick = async () => {
    setImporting(true);
    try {
      await handleImport();
    } finally {
      setImporting(false);
    }
  };

  const renderMatchStep = () => (
    <MatchBeforeCreate
      hits={matchHits}
      entityType="stakeholder"
      selectedId={selectedMatchId}
      onSelect={setSelectedMatchId}
      canAttach={!!onMerge}
      attaching={attachingToMatch}
      error={matchError}
      onOpenExisting={handleOpenExisting}
      onAttach={handleAttachToExisting}
      onCreateAnyway={handleCreateAnyway}
    />
  );

  const renderPreviewStep = () => {
    if (!extractedData) return null;

    return (
      <>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-5 pt-3 sm:px-5">
          {previewUrl && (
            <img
              src={previewUrl}
              alt="Business card"
              className="mb-4 w-[132px] rounded-xl object-cover ring-1 ring-black/[0.06]"
              style={{ aspectRatio: `${CARD_ASPECT_RATIO}` }}
            />
          )}
          <ExtractedDataPreview
            extractedData={extractedData}
            onFieldEdit={handleFieldEdit}
          />
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={handleTryAnother} className="h-10 flex-1 sm:h-9 sm:flex-none">
            <Camera className="h-4 w-4" strokeWidth={1.75} />
            Try another
          </Button>
          <Button variant="primary" onClick={handleImportClick} loading={importing} className="h-10 flex-1 sm:h-9 sm:flex-none">
            {!importing && <Check className="h-4 w-4" strokeWidth={2} />}
            Import
          </Button>
        </DialogFooter>
      </>
    );
  };

  const getTitle = () => {
    switch (step) {
      case 'capture': return 'Scan card';
      case 'extract': return error && !extracting ? "Couldn't read card" : 'Reading card';
      case 'match': return 'Already on file?';
      case 'preview': return 'Review';
    }
  };

  return (
    <>
      {step === 'capture' ? (
        createPortal(renderCaptureOverlay(), document.body)
      ) : (
        <Dialog
          open={true}
          onClose={onClose}
          labelledBy="card-import-title"
          handle
          className={`sm:max-w-lg ${step === 'preview' ? 'h-[92dvh] sm:h-auto sm:max-h-[84vh]' : 'max-h-[92dvh] sm:max-h-[84vh]'}`}
        >
          <div className="flex flex-shrink-0 items-center justify-between gap-4 px-5 pt-1 sm:pt-5">
            <h2 id="card-import-title" className="text-[18px] font-normal tracking-[-0.02em] text-ink">
              {getTitle()}
            </h2>
            <IconButton label="Close" size="sm" onClick={onClose} className="-mr-1 bg-neutral-100 text-neutral-600">
              <X className="h-4 w-4" strokeWidth={1.75} />
            </IconButton>
          </div>
          {step === 'extract' && renderExtractStep()}
          {step === 'match' && renderMatchStep()}
          {step === 'preview' && renderPreviewStep()}
        </Dialog>
      )}

      {/* Duplicate Review Sheet */}
      {showDuplicateReview && pendingStakeholderData && (
        <DuplicateReviewSheet
          open={showDuplicateReview}
          onClose={() => setShowDuplicateReview(false)}
          matches={duplicateMatches}
          newData={pendingStakeholderData}
          onSkip={handleDuplicateSkip}
          onMerge={handleDuplicateMerge}
        />
      )}
    </>
  );
};
